import { useEffect, useRef, useState } from "react";
import iconPhone from "../../../assets/img/icon-phone.png";
import iconEmail from "../../../assets/img/icon-email.png";
import { useSelector } from "react-redux";
import { Container } from "react-bootstrap";
import { useSearchParams } from "react-router-dom";
import ReportIssue from "../help/reportIssue";
import GlobalPopup from "../GlobalPopup/globalPopup";
import { usePermission } from "../../../helpers/hooks/usePermissions";
import PageTitle from "../layout/pageTitle/pageTitle";
import { getResourcePdfs, getTeamMembers, getOnboardingStatuses, getOnboardingProgress, getOnboardingCustomers } from "../../../store/api/apiSlice";
import { useAppDispatch } from "../../../store/hooks";
import axios from "../../../services/httpService";
import { Pie } from "react-chartjs-2";
import Chart from "chart.js/auto";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { AgGridReact } from "ag-grid-react";
import { ModuleRegistry, ClientSideRowModelModule, ColDef } from "ag-grid-community";
import { MenuModule, MultiFilterModule, SetFilterModule } from "ag-grid-enterprise";

Chart.register(ChartDataLabels);
ModuleRegistry.registerModules([ClientSideRowModelModule, MenuModule, MultiFilterModule, SetFilterModule]);

const API_URL = (axios as any).Api as string;
const USERMANAGEAPI = ((axios as any).UserManageAPI || "") as string;
const CUSTOMER_SERVICE_PHOTO_ID = "13087650";
const isCustomerServiceMember = (title?: string) => title?.toLowerCase().includes("dedicated support experts") ?? false;

type NavSection = "meet-your-team" | "onboarding-timeline" | "resources-all" | "resources-onsite" | "resources-bns" | "resources-portal";

interface PdfDocument {
  blobName: string;
  name: string;
}

interface TeamMember {
  name: string;
  title?: string;
  intro?: string;
  email?: string;
  phone?: string;
  photoUrl?: string;
}

interface OnboardingStatus {
  statusId: number;
  sortOrder: number;
  statusName: string;
  descriptionGoal?: string;
  exitCriteria?: string;
}

interface OnboardingProgressItem {
  statusName: string;
  count: number;
  percentage: number;
}

interface OnboardingCustomer {
  customerId: string;
  customerName: string;
  hierarchy?: string;
  status?: string;
  collectionOption?: string;
  firstScheduled?: string;
  firstReturn?: string;
  lastPickup?: string;
  lastReturn?: string;
}

const mmddyyyyComparator = (a?: string, b?: string) => {
  const t1 = a ? new Date(a).getTime() : 0;
  const t2 = b ? new Date(b).getTime() : 0;
  return t1 - t2;
};

const ONBOARDING_CUSTOMER_COLS: ColDef<OnboardingCustomer>[] = [
  { headerName: "Customer ID",       field: "customerId",       filter: "agTextColumnFilter", filterParams: { buttons: ["reset"], suppressAndOrCondition: true }, menuTabs: ["filterMenuTab"] },
  { headerName: "Customer Name",     field: "customerName",     filter: "agTextColumnFilter", filterParams: { buttons: ["reset"], suppressAndOrCondition: true }, menuTabs: ["filterMenuTab"] },
  { headerName: "Hierarchy",         field: "hierarchy",        filter: "agTextColumnFilter", filterParams: { buttons: ["reset"], suppressAndOrCondition: true }, menuTabs: ["filterMenuTab"] },
  { headerName: "Status",            field: "status",           filter: "agTextColumnFilter", filterParams: { buttons: ["reset"], suppressAndOrCondition: true }, menuTabs: ["filterMenuTab"] },
  { headerName: "Collection Option", field: "collectionOption", filter: "agTextColumnFilter", filterParams: { buttons: ["reset"], suppressAndOrCondition: true }, menuTabs: ["filterMenuTab"] },
  { headerName: "First Scheduled",   field: "firstScheduled",   filter: "agTextColumnFilter", comparator: mmddyyyyComparator, filterParams: { buttons: ["reset"], suppressAndOrCondition: true }, menuTabs: ["filterMenuTab"] },
  { headerName: "First Return",      field: "firstReturn",      filter: "agTextColumnFilter", comparator: mmddyyyyComparator, filterParams: { buttons: ["reset"], suppressAndOrCondition: true }, menuTabs: ["filterMenuTab"] },
  { headerName: "Last Pickup",       field: "lastPickup",       filter: "agTextColumnFilter", comparator: mmddyyyyComparator, filterParams: { buttons: ["reset"], suppressAndOrCondition: true }, menuTabs: ["filterMenuTab"] },
  { headerName: "Last Return",       field: "lastReturn",       filter: "agTextColumnFilter", comparator: mmddyyyyComparator, filterParams: { buttons: ["reset"], suppressAndOrCondition: true }, menuTabs: ["filterMenuTab"] },
];

const FALLBACK_EMAIL = "customer.service@pharmalogistics.com";

const RESOURCE_SECTIONS: NavSection[] = ["resources-all", "resources-onsite", "resources-bns", "resources-portal"];


function normalizeNewlines(text: string | undefined): string {
  return (text ?? "").replace(/\\n/g, "\n");
}

function getTeamMemberPriority(title: string | undefined): number {
  const t = (title ?? "").toLowerCase();
  if (t.includes("customer success")) return 0;
  if (t.includes("fsr") || t.includes("field service")) return 1;
  if (t.includes("dedicated support experts")) return 3;
  return 2;
}

function sortTeamMembers(members: TeamMember[]): TeamMember[] {
  return [...members].sort((a, b) => getTeamMemberPriority(a.title) - getTeamMemberPriority(b.title));
}

function getFilteredPdfs(docs: PdfDocument[], section: NavSection): PdfDocument[] {
  if (section === "resources-all")    return docs;
  if (section === "resources-onsite") return docs.filter(d => d.name.includes("_onsite"));
  if (section === "resources-bns")    return docs.filter(d => d.name.includes("_bns"));
  if (section === "resources-portal") return docs.filter(d => d.name.includes("_portal"));
  return [];
}

export const Resources = () => {
  usePermission(true);
  const dispatch = useAppDispatch();
  const sfId: number = useSelector((state: any) => state?.auth?.registrationDetails?.customerId) ?? 0;
  const supportEmail: string = useSelector((state: any) => state?.auth?.registrationDetails?.supportEmail) ?? "customer.service@pharmalogistics.com";

  const [showReportIssue, setShowReportIssue] = useState(false);
  const [showReportIssueSuccess, setShowReportIssueSuccess] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get("reportIssue") === "open") {
      setShowReportIssue(true);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams]);

  const [allPdfs, setAllPdfs] = useState<PdfDocument[]>([]);
  const [activeSection, setActiveSection] = useState<NavSection>("resources-all");
  const initialFocusDoneRef = useRef(false);
  const [resourcesExpanded, setResourcesExpanded] = useState(true);
  const [thumbnailUrls, setThumbnailUrls] = useState<Record<string, string>>({});
  const objectUrlsRef = useRef<string[]>([]);
  const thumbnailFetchedRef = useRef(false);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [teamMembersLoaded, setTeamMembersLoaded] = useState(false);
  const teamFetchedRef = useRef(false);
  const [photoUrls, setPhotoUrls] = useState<Record<string, string>>({});
  const photoUrlsRef = useRef<string[]>([]);
  const [onboardingStatuses, setOnboardingStatuses] = useState<OnboardingStatus[]>([]);
  const onboardingFetchedRef = useRef(false);
  const [onboardingProgress, setOnboardingProgress] = useState<OnboardingProgressItem[]>([]);
  const progressFetchedRef = useRef(false);
  const [customerGridApi, setCustomerGridApi] = useState<any>(null);
  const [customerGridData, setCustomerGridData] = useState({
    rows: [] as OnboardingCustomer[],
    totalRows: 0,
    pageSize: 100,
    page: 1,
    globalSearch: "",
    sortField: "",
    sortOrder: "",
    isForExcel: false,
    rowsPerPageOptions: [100, 200, 300],
  });
  const customerGridRef = useRef<any>(null);
  const customersFetchedRef = useRef(false);
  const [selectedChartStatus, setSelectedChartStatus] = useState<string | null>(null);
  const chartSectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    dispatch(getResourcePdfs()).then((result: any) => {
      setAllPdfs(result?.payload?.data ?? []);
    });
    return () => {
      objectUrlsRef.current.forEach((u) => URL.revokeObjectURL(u));
      photoUrlsRef.current.forEach((u) => URL.revokeObjectURL(u));
    };
  }, []);

  useEffect(() => {
    if (allPdfs.length === 0 || thumbnailFetchedRef.current) return;
    thumbnailFetchedRef.current = true;
    const newUrls: Record<string, string> = {};
    Promise.all(
      allPdfs.map(async (pdf) => {
        try {
          const response = await axios.get(
            `${API_URL}${USERMANAGEAPI}Resources/GetPdfThumbnail?blobName=${encodeURIComponent(pdf.blobName)}`,
            { responseType: "blob" }
          );
          const url = URL.createObjectURL(response.data);
          objectUrlsRef.current.push(url);
          newUrls[pdf.blobName] = url;
        } catch {
          // placeholder remains for this card
        }
      })
    ).then(() => setThumbnailUrls(newUrls));
  }, [allPdfs]);

  const handlePdfClick = async (pdf: PdfDocument) => {
    try {
      const response = await axios.get(
        `${API_URL}${USERMANAGEAPI}Resources/DownloadResourcePdf?blobName=${encodeURIComponent(pdf.blobName)}`,
        { responseType: "blob" }
      );
      const blobUrl = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = pdf.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch {
      // silently ignore — blob not found or auth error
    }
  };

  useEffect(() => {
    if (teamFetchedRef.current || !sfId) return;
    teamFetchedRef.current = true;
    dispatch(getTeamMembers(sfId)).then((result: any) => {
      setTeamMembers(sortTeamMembers(result?.payload?.data ?? []));
      setTeamMembersLoaded(true);
    });
  }, [sfId]);

  const hasCsm = teamMembers.some((m) => m.title?.toLowerCase().includes("customer success manager"));
  const showTeamSection = teamMembersLoaded && hasCsm;

  useEffect(() => {
    if (!teamMembersLoaded) return;
    if (!initialFocusDoneRef.current) {
      initialFocusDoneRef.current = true;
      if (hasCsm && activeSection === "resources-all") {
        setActiveSection("meet-your-team");
        return;
      }
    }
    if (!hasCsm && (activeSection === "meet-your-team" || activeSection === "onboarding-timeline")) {
      setActiveSection("resources-all");
    }
  }, [teamMembersLoaded, hasCsm, activeSection]);

  useEffect(() => {
    if (!showTeamSection) return;
    const photoKeys = teamMembers.map((m) =>
      isCustomerServiceMember(m.title) ? CUSTOMER_SERVICE_PHOTO_ID : m.photoUrl
    ).filter((key): key is string => !!key);
    const uniqueKeys = Array.from(new Set(photoKeys));

    uniqueKeys.forEach(async (photoKey) => {
      try {
        const response = await axios.get(
          `${API_URL}${USERMANAGEAPI}Resources/GetTeamMemberPhoto?fileId=${encodeURIComponent(photoKey)}`,
          { responseType: "blob" }
        );
        const url = URL.createObjectURL(response.data);
        photoUrlsRef.current.push(url);
        setPhotoUrls((prev) => ({ ...prev, [photoKey]: url }));
      } catch {
        // placeholder shown for this member
      }
    });
  }, [teamMembers, showTeamSection]);

  useEffect(() => {
    if (activeSection !== "onboarding-timeline") return;
    if (!onboardingFetchedRef.current) {
      onboardingFetchedRef.current = true;
      dispatch(getOnboardingStatuses()).then((result: any) => {
        setOnboardingStatuses(result?.payload?.data ?? []);
      });
    }
    if (!progressFetchedRef.current) {
      progressFetchedRef.current = true;
      dispatch(getOnboardingProgress(sfId)).then((result: any) => {
        setOnboardingProgress(result?.payload?.data ?? []);
      });
    }
    if (!customersFetchedRef.current) {
      customersFetchedRef.current = true;
      dispatch(getOnboardingCustomers(sfId)).then((result: any) => {
        const rows = result?.payload?.data ?? [];
        setCustomerGridData((prev) => ({ ...prev, rows, totalRows: rows.length }));
      });
    }
  }, [activeSection]);

  const onCustomerGridReady = (params: any) => setCustomerGridApi(params.api);

  const resetChartFilter = () => {
    setSelectedChartStatus(null);
    customerGridApi?.setFilterModel(null);
  };

  const onCustomerClearFilter = () => {
    resetChartFilter();
    setCustomerGridData((prev) => ({ ...prev, globalSearch: "" }));
  };

  useEffect(() => {
    if (!selectedChartStatus) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (chartSectionRef.current && !chartSectionRef.current.contains(e.target as Node)) {
        resetChartFilter();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [selectedChartStatus, customerGridApi]);

  const currentPdfs = getFilteredPdfs(allPdfs, activeSection);
  const showPdfs = RESOURCE_SECTIONS.includes(activeSection);

  return (
    <Container fluid>
      <div className="resources-page-header">
        <PageTitle name="Welcome to Your Customer Success Hub" />
      </div>
      <ReportIssue
        show={showReportIssue}
        handleClose={() => setShowReportIssue(false)}
        setShow={setShowReportIssue}
        setSuccessShow={setShowReportIssueSuccess}
      />
      <GlobalPopup
        show={showReportIssueSuccess}
        showImage={true}
        handleClose={() => setShowReportIssueSuccess(false)}
        doneButton={true}
        message="Your issue has been sent successfully. customer support team will contact you shortly."
      />

      {/* White card with grey outline */}
      <div className="resources-outer-container">
        <div className="resources-layout">

          {/* Left navigation */}
          <nav className="resources-sidenav">
            <ul>
              {showTeamSection && (
                <li
                  className={activeSection === "meet-your-team" ? "active" : ""}
                  onClick={() => setActiveSection("meet-your-team")}
                >
                  Meet your Team
                </li>
              )}
              {showTeamSection && (
                <li
                  className={activeSection === "onboarding-timeline" ? "active" : ""}
                  onClick={() => setActiveSection("onboarding-timeline")}
                >
                  Onboarding Timeline
                </li>
              )}
              <li className="resources-sidenav-parent">
                <div
                  className={`resources-sidenav-group${activeSection === "resources-all" ? " active" : ""}`}
                  onClick={() => {
                    setActiveSection("resources-all");
                    setResourcesExpanded(true);
                  }}
                >
                  Resources
                  <i
                    className={`fa-solid fa-chevron-${resourcesExpanded ? "up" : "down"}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setResourcesExpanded(prev => !prev);
                    }}
                  />
                </div>
                {resourcesExpanded && (
                  <ul className="resources-subnav">
                    <li
                      className={activeSection === "resources-onsite" ? "active" : ""}
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveSection("resources-onsite");
                      }}
                    >
                      On-site Resources
                    </li>
                    <li
                      className={activeSection === "resources-bns" ? "active" : ""}
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveSection("resources-bns");
                      }}
                    >
                      Box and Ship Resources
                    </li>
                    <li
                      className={activeSection === "resources-portal" ? "active" : ""}
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveSection("resources-portal");
                      }}
                    >
                      Portal Resources
                    </li>
                  </ul>
                )}
              </li>
            </ul>
          </nav>

          {/* Main content */}
          <div className="resources-content">
            {activeSection === "meet-your-team" && showTeamSection && (
              <>
              <div className="resources-team-banner">
                <h2 className="resources-team-banner-title">Delighting Customers Starts Here</h2>
                <p className="resources-team-banner-desc">Meet the team dedicated to supporting you at every step! Together, we're here to answer questions, guide you through every stage of the returns process, and help you get the most out of your partnership with Pharma Logistics.</p>
                <p className="resources-team-banner-tagline">Delighting Customers is our Passion. Compliance is our Priority.®</p>
              </div>
              <div className={`resources-team-grid${teamMembers.length > 0 && teamMembers.length < 3 ? " resources-team-grid--centered" : ""}`}>
                {teamMembers.length === 0 && (
                  <p className="resources-placeholder-text">Loading team members...</p>
                )}
                {teamMembers.map((member, idx) => {
                  const photoKey = isCustomerServiceMember(member.title) ? CUSTOMER_SERVICE_PHOTO_ID : member.photoUrl;
                  return (
                  <div key={idx} className="resources-team-card">
                    <div className="resources-team-photo">
                      {photoKey && photoUrls[photoKey] ? (
                        <img src={photoUrls[photoKey]} alt={member.name} />
                      ) : (
                        <div className="resources-team-photo-placeholder">
                          <i className="fa-solid fa-user" />
                        </div>
                      )}
                    </div>
                    <div className="resources-team-name">{member.name}</div>
                    {member.title && <div className="resources-team-title">{member.title}</div>}
                    {(() => {
                        const displayIntro = isCustomerServiceMember(member.title)
                          ? "When you need us, you can count on us. Our Customer Service Team is here to ensure every interaction with Pharma Logistics is responsive, seamless, and focused on your success. Whether you have account questions, need shipment updates, require portal assistance, or need help with returns, you can count on us to provide prompt responses, timely solutions, and the exceptional service that's made Pharma Logistics a trusted partner for 30 years."
                          : member.intro;
                        return displayIntro ? <div className="resources-team-intro">{displayIntro}</div> : null;
                      })()}
                    <div className="resources-team-actions">
                      {(() => {
                        const displayEmail = isCustomerServiceMember(member.title)
                          ? "customer.service@pharmalogistics.com"
                          : member.email;
                        return displayEmail ? (
                          <a href={`mailto:${displayEmail}`} className="resources-team-email-link">
                            <img src={iconEmail} alt="" className="resources-contact-icon" />
                            {displayEmail}
                          </a>
                        ) : null;
                      })()}
                      {(() => {
                        const displayPhone = isCustomerServiceMember(member.title) ? "888-729-7427" : member.phone;
                        return displayPhone ? (
                          <span className="resources-team-phone">
                            <img src={iconPhone} alt="" className="resources-contact-icon" />
                            {displayPhone}
                          </span>
                        ) : null;
                      })()}
                    </div>
                  </div>
                  );
                })}
              </div>
              </>
            )}
            {activeSection === "onboarding-timeline" && showTeamSection && (
              <div className="resources-onboarding-wrapper">
                <div className="resources-team-banner" style={{ marginBottom: 24 }}>
                  <h2 className="resources-team-banner-title">Onboarding Timeline</h2>
                </div>
                <div className="resources-onboarding-top-row">
                  {/* Pie chart */}
                  <div className="resources-onboarding-chart-section" ref={chartSectionRef}>
                    {onboardingProgress.length > 0 ? (() => {
                      const onboardedPct = onboardingProgress.find(
                        (p) => p.statusName.toLowerCase().includes("onboarded")
                      )?.percentage ?? 0;
                      const PIE_COLORS = ["#000351", "#B30000", "#4472C4", "#C2D500", "#F4A261", "#6A4C93"];
                      const pieData = {
                        labels: onboardingProgress.map((p) => p.statusName),
                        datasets: [{
                          data: onboardingProgress.map((p) => p.count),
                          backgroundColor: onboardingProgress.map((_, i) => PIE_COLORS[i % PIE_COLORS.length]),
                          borderWidth: onboardingProgress.map((p) => selectedChartStatus === p.statusName ? 3 : 0),
                          borderColor: onboardingProgress.map((p) => selectedChartStatus === p.statusName ? "#fff" : "transparent"),
                          offset: onboardingProgress.map((p) => selectedChartStatus === p.statusName ? 12 : 0),
                        }],
                      };
                      const pieOptions: any = {
                        responsive: true,
                        onClick: (_: any, elements: any[]) => {
                          if (elements.length > 0) {
                            const status = onboardingProgress[elements[0].index].statusName;
                            if (selectedChartStatus === status) {
                              resetChartFilter();
                            } else {
                              setSelectedChartStatus(status);
                              customerGridApi?.setFilterModel({
                                status: { filterType: "text", type: "equals", filter: status },
                              });
                            }
                          } else {
                            resetChartFilter();
                          }
                        },
                        plugins: {
                          legend: { position: "bottom" as const, labels: { boxWidth: 14, font: { size: 12 } } },
                          tooltip: {
                            callbacks: {
                              label: (ctx: any) => {
                                const item = onboardingProgress[ctx.dataIndex];
                                return ` ${item.statusName}: ${item.count} (${item.percentage}%)`;
                              },
                            },
                          },
                          datalabels: {
                            color: "#fff",
                            font: { size: 11, weight: "bold" as const },
                            clamp: true,
                            display: "auto",
                            formatter: (_: any, ctx: any) => {
                              const item = onboardingProgress[ctx.dataIndex];
                              return item.percentage >= 5 ? `${item.count}\n${item.percentage}%` : "";
                            },
                          },
                        },
                      };
                      return (
                        <>
                          <div className="resources-onboarding-chart-title">
                            Onboarding Progress &mdash; {onboardedPct}% Onboarded
                          </div>
                          <div className={`resources-onboarding-chart${selectedChartStatus ? " resources-onboarding-chart--filtered" : ""}`}>
                            <Pie data={pieData} options={pieOptions} />
                          </div>
                          {selectedChartStatus && (
                            <div className="resources-onboarding-chart-filter-hint">
                              Filtering: <strong>{selectedChartStatus}</strong>
                            </div>
                          )}
                        </>
                      );
                    })() : (
                      <p className="resources-placeholder-text">Loading onboarding progress...</p>
                    )}
                  </div>

                  {/* Status guide table */}
                  <div className="resources-onboarding-status-section">
                    <div className="resources-onboarding-chart-title">Onboarding Progress Tracker</div>
                    <p className="resources-onboarding-section-desc">Easily track where each account is in the onboarding process. See completed milestones, current status, and upcoming steps so you always know what's next.</p>
                    {onboardingStatuses.length > 0 ? (
                      <table className="resources-onboarding-table resources-onboarding-guide-table">
                        <thead>
                          <tr>
                            <th>Phase</th>
                            <th>What This Means</th>
                            <th>Next Step</th>
                          </tr>
                        </thead>
                        <tbody>
                          {onboardingStatuses.map((s) => (
                            <tr key={s.statusId}>
                              <td className="resources-onboarding-status-name">{s.statusName}</td>
                              <td>{normalizeNewlines(s.descriptionGoal)}</td>
                              <td>{normalizeNewlines(s.exitCriteria)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <p className="resources-placeholder-text">Loading onboarding timeline...</p>
                    )}
                  </div>
                </div>

                <div className="resources-onboarding-grid-wrapper">
                  <div className="resources-grid-toolbar content_body_head">
                    <div className="resources-onboarding-grid-heading">Customer Onboarding Status Tracker</div>
                    <div className="resources-grid-toolbar-actions">
                      <div className="resources-grid-search">
                        <i className="fa-solid fa-search" />
                        <input
                          type="text"
                          placeholder="Search"
                          value={customerGridData.globalSearch}
                          onChange={(e) => setCustomerGridData((prev) => ({ ...prev, globalSearch: e.target.value }))}
                        />
                      </div>
                      <button
                        className="btn btn-sm btn-outline-dark"
                        title="Clear all filters"
                        onClick={onCustomerClearFilter}
                      >
                        <i className="fa-solid fa-filter-circle-xmark" />
                      </button>
                    </div>
                  </div>
                  <div className="ag-theme-quartz resources-grid-body">
                    <AgGridReact
                      ref={customerGridRef}
                      rowData={customerGridData.rows}
                      columnDefs={ONBOARDING_CUSTOMER_COLS}
                      onGridReady={onCustomerGridReady}
                      quickFilterText={customerGridData.globalSearch}
                      domLayout="autoHeight"
                      autoSizeStrategy={{ type: "fitGridWidth" }}
                      pagination={true}
                      paginationPageSize={customerGridData.pageSize}
                      paginationPageSizeSelector={customerGridData.rowsPerPageOptions}
                      suppressPaginationPanel={false}
                    />
                  </div>
                </div>
              </div>
            )}
            {showPdfs && (
              <>
              <div className="resources-team-banner" style={{ marginBottom: 24 }}>
                <h2 className="resources-team-banner-title">
                  {activeSection === "resources-onsite" ? "On-site Resources"
                    : activeSection === "resources-bns" ? "Box and Ship Resources"
                    : activeSection === "resources-portal" ? "Portal Resources"
                    : "Resources"}
                </h2>
              </div>
              <div className="resources-pdf-scroll">
                <div className="resources-pdf-grid">
                  {currentPdfs.map((pdf) => (
                    <div key={pdf.blobName} className="resources-pdf-card" onClick={() => handlePdfClick(pdf)}>
                      <div className="resources-pdf-preview">
                        {thumbnailUrls[pdf.blobName] ? (
                          <img src={thumbnailUrls[pdf.blobName]} alt={pdf.name} />
                        ) : (
                          <div className="resources-pdf-placeholder-icon">
                            <i className="fa-regular fa-file-pdf" />
                            <span>PDF Preview</span>
                          </div>
                        )}
                      </div>
                      <div className="resources-pdf-title">{pdf.name}</div>
                    </div>
                  ))}
                </div>
              </div>
              </>
            )}
          </div>
        </div>

        {/* Static footer — always visible */}
        {(() => {
          const csmEmail = teamMembers.find(m => m.title?.toLowerCase().includes("customer success manager"))?.email || FALLBACK_EMAIL;
          return (
            <div className="resources-footer">
              <div className="resources-footer-csm">
                Questions: Please{" "}
                <a href={`mailto:${csmEmail}?subject=Question%20for%20My%20Customer%20Success%20Manager`} target="_blank" rel="noreferrer">contact</a>{" "}
                your Dedicated Customer Success Manager
              </div>
              <div className="resources-footer-contact">
                <span><i className="fa-solid fa-phone" /> <a href="tel:8887297427">888-729-7427</a></span>
                <span><i className="fa-solid fa-envelope" /> <a href={`mailto:${supportEmail}`}>{supportEmail}</a></span>
                <span><i className="fa-solid fa-location-dot" /> 1801 N Butterfield, Libertyville, IL 60048</span>
                <span><i className="fa-solid fa-clock" /> Monday &ndash; Friday: 7 AM &ndash; 4 PM CST</span>
              </div>
            </div>
          );
        })()}
      </div>
    </Container>
  );
};

export default Resources;
